import { AdminActivity, REGISTRATION_STATUS_LABELS, RegistrationStatus, WEBINAR_STATUS_LABELS, WebinarStatus, reopensWebinar } from "./types";

// Plain-language lines for the admin audit log (public.admin_audit_log).

export type ActivityGroup = "changes" | "emails" | "security";

export const activityGroup = (entry: AdminActivity): ActivityGroup =>
  entry.action.startsWith("emails.") ? "emails"
  : entry.action.startsWith("admin.") || entry.action.startsWith("rate_limit.") || entry.action.startsWith("access.") ? "security"
  : "changes";

/** Blocked or refused requests: what an administrator should look at */
export const needsReview = (entry: AdminActivity) => entry.outcome !== "ok";

/** Webinar fields, as the editor names them */
const FIELD_LABELS: Record<string, string> = {
  title: "title",
  slug: "web address",
  summary: "summary",
  description: "description",
  topics: "topics",
  presenter_name: "presenter",
  presenter_role: "presenter's role",
  presenter_photo: "presenter photo",
  start_at: "start",
  end_at: "end",
  registration_deadline: "registration deadline",
  capacity: "seats",
  status: "status",
  meeting_platform: "platform",
  recording_url: "recording link",
  resource_url: "resources link",
};

const text = (value: unknown) => (typeof value === "string" ? value : "");
const count = (value: unknown) => (typeof value === "number" ? value : 0);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const webinarStatus = (value: unknown) => WEBINAR_STATUS_LABELS[value as WebinarStatus] ?? text(value);
const registrationStatus = (value: unknown) => (REGISTRATION_STATUS_LABELS[value as RegistrationStatus] ?? text(value)).toLowerCase();

const LIMIT_KINDS: Record<string, string> = {
  bulk: "joining-instruction sends",
  single: "single joining-instruction sends",
  retry: "retries of failed emails",
};

/**
 * One line describing the entry. `webinarTitle` is the webinar's current title, for entries
 * that only stored its ID.
 */
export function describeActivity(entry: AdminActivity, webinarTitle?: string): string {
  const d = entry.details;
  const title = text(d.title) || webinarTitle || "a webinar";
  const quoted = `“${title}”`;

  switch (entry.action) {
    case "webinar.created":
      return `Added the webinar ${quoted} (${webinarStatus(d.status)})`;
    case "webinar.updated": {
      const fields = (Array.isArray(d.changed) ? d.changed : []).filter(f => f !== "status").map(f => FIELD_LABELS[f as string] ?? String(f).replace(/_/g, " "));
      if (d.status_to) {
        const from = d.status_from as WebinarStatus;
        const to = d.status_to as WebinarStatus;
        const round = reopensWebinar(from, to) ? "; a new round started and earlier attendees were archived" : "";
        return `Changed ${quoted} from ${webinarStatus(from)} to ${webinarStatus(to)}${fields.length ? `; also edited: ${fields.join(", ")}` : ""}${round}`;
      }
      return fields.length ? `Edited ${quoted}: ${fields.join(", ")}` : `Edited ${quoted}`;
    }
    case "joining_link.saved":
      return `Saved the joining link for ${quoted}`;
    case "joining_link.removed":
      return `Removed the joining link for ${quoted}`;
    case "registration.status_changed":
      return d.status_to === "registered"
        ? `Restored registration ${text(d.reference)} (was ${registrationStatus(d.status_from)})`
        : `Marked registration ${text(d.reference)} as ${registrationStatus(d.status_to)}`;
    case "registration.sent_by_hand":
      return d.recorded === false
        ? `Cleared the "sent by hand" record for ${text(d.reference)}`
        : `Recorded joining instructions sent by hand to ${text(d.reference)}`;
    case "emails.send_joining": {
      const who = typeof d.selected === "number" ? (d.selected === 1 ? "one person" : `${d.selected} selected people`) : "everyone not sent yet";
      return `${d.resend ? "Re-sent" : "Sent"} joining instructions for ${quoted} to ${d.resend && d.selected === null ? "everyone" : who}: ${plural(count(d.queued), "email")} queued`;
    }
    case "emails.retry_failed":
      return `Retried failed emails${entry.webinarId ? ` for ${quoted}` : ""}: ${plural(count(d.requeued), "email")} back in the queue`;
    case "admin.signed_in":
      // new_authenticator: entries from when sign-in also needed an authenticator code
      return d.new_authenticator ? "Set up the authenticator app and signed in" : "Signed in";
    // Shown with a "Blocked" or "Refused" label, so the sentences don't repeat it
    case "rate_limit.registration":
      return d.limit === "email"
        ? `Too many registration attempts for one email address (${quoted})`
        : `Too many registration attempts from one connection (${quoted})`;
    case "rate_limit.admin_send":
      return `Too many ${LIMIT_KINDS[text(d.kind)] ?? "sends"} in an hour (limit ${count(d.limit)})`;
    case "access.send_emails":
      return "Email sending by an account that isn't an administrator";
    default:
      return entry.action;
  }
}
